import { CommonModule } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';

import { FormsModule } from '@angular/forms';

import { DeleteRequest } from '../../../../models/app.models';

import {
  WhitelistItem,
  WhitelistService
} from '../../../../services/whitelist.service';

import { ToastService } from '../../../../services/toast.service';


@Component({
  selector: 'app-admin-whitelist',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-whitelist.component.html',
  styleUrls: [
    './admin-whitelist.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminWhitelistComponent
  implements OnInit {

  readonly requiredFieldsMessage =
    'Complete all required fields.';

  whitelistItems: WhitelistItem[] = [];

  whitelistItemsLoading = true;

  whitelistEditorOpen = false;

  whitelistDraft: WhitelistItem =
    this.emptyWhitelistItem();

  selectedWhitelistDocument:
    File | null = null;

  deleteRequest: DeleteRequest | null = null;


  constructor(
    private readonly whitelistService:
      WhitelistService,
    private readonly toastService:
      ToastService,
    private readonly changeDetector:
      ChangeDetectorRef
  ) {}


  ngOnInit(): void {
    this.loadWhitelistItems();
  }


  loadWhitelistItems(): void {
    this.whitelistItemsLoading = true;

    this.whitelistService
      .getAll()
      .subscribe({
        next: items => {
          this.whitelistItems =
            items.sort(
              (a, b) =>
                a.role.localeCompare(b.role) ||
                a.category.localeCompare(
                  b.category
                ) ||
                a.displayOrder -
                  b.displayOrder ||
                a.name.localeCompare(b.name)
            );

          this.whitelistItemsLoading = false;

          this.changeDetector.markForCheck();
        },

        error: error => {
          console.error(
            'Unable to load portal resources:',
            error
          );

          this.whitelistItemsLoading = false;

          this.toastService.error(
            'Unable to load portal resources.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }


  openWhitelistEditor(
    item?: WhitelistItem
  ): void {
    this.whitelistDraft = item
      ? { ...item }
      : this.emptyWhitelistItem();

    this.selectedWhitelistDocument = null;

    this.whitelistEditorOpen = true;
  }


  closeWhitelistEditor(): void {
    this.whitelistDraft =
      this.emptyWhitelistItem();

    this.selectedWhitelistDocument = null;

    this.whitelistEditorOpen = false;
  }


  onWhitelistDocumentSelected(
    event: Event
  ): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    if (!file) {
      return;
    }

    const isPdf =
      file.type === 'application/pdf' ||
      file.name
        .toLowerCase()
        .endsWith('.pdf');

    if (!isPdf) {
      this.toastService.error(
        'Please select a PDF document.'
      );

      input.value = '';

      return;
    }

    if (
      file.size >
      10 * 1024 * 1024
    ) {
      this.toastService.error(
        'PDF documents cannot exceed 10 MB.'
      );

      input.value = '';

      return;
    }

    this.selectedWhitelistDocument =
      file;

    this.changeDetector.markForCheck();
  }


  saveWhitelistItem(): void {
    if (
      !this.whitelistDraft.name.trim() ||
      !this.whitelistDraft.role ||
      !this.whitelistDraft.category.trim()
    ) {
      this.toastService.error(
        this.requiredFieldsMessage
      );

      return;
    }

    const document =
      this.selectedWhitelistDocument;

    if (this.whitelistDraft.id) {
      this.whitelistService
        .update(
          this.whitelistDraft
        )
        .subscribe({
          next: () => {
            if (document) {
              this.uploadWhitelistDocument(
                this.whitelistDraft.id,
                document
              );

              return;
            }

            this.finishWhitelistSave();
          },

          error: error => {
            console.error(
              'Unable to update portal resource:',
              error
            );

            this.toastService.error(
              'Unable to save portal resource.'
            );

            this.changeDetector.markForCheck();
          }
        });

      return;
    }

    const createRequest = {
      name:
        this.whitelistDraft.name,

      url:
        this.whitelistDraft.url,

      role:
        this.whitelistDraft.role,

      category:
        this.whitelistDraft.category,

      displayOrder:
        this.whitelistDraft.displayOrder,

      isActive:
        this.whitelistDraft.isActive
    };

    this.whitelistService
      .create(createRequest)
      .subscribe({
        next: savedItem => {
          if (document) {
            this.uploadWhitelistDocument(
              savedItem.id,
              document
            );

            return;
          }

          this.finishWhitelistSave();
        },

        error: error => {
          console.error(
            'Unable to create portal resource:',
            error
          );

          this.toastService.error(
            'Unable to save portal resource.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }


  removeWhitelistDocument(): void {
    if (
      !this.whitelistDraft.id ||
      !this.whitelistDraft.documentId
    ) {
      return;
    }

    this.whitelistService
      .deleteDocument(
        this.whitelistDraft.id
      )
      .subscribe({
        next: () => {
          this.whitelistDraft.documentId =
            null;

          this.selectedWhitelistDocument =
            null;

          this.toastService.success(
            'Document removed.'
          );

          this.loadWhitelistItems();

          this.changeDetector.markForCheck();
        },

        error: error => {
          console.error(
            'Unable to remove portal document:',
            error
          );

          this.toastService.error(
            'Unable to remove document.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }


  requestDelete(
    kind: DeleteRequest['kind'],
    id: number,
    label: string
  ): void {
    this.deleteRequest = {
      kind,
      id,
      label
    };
  }


  confirmDelete(): void {
    if (!this.deleteRequest) {
      return;
    }

    this.deleteWhitelistItem(
      this.deleteRequest.id
    );
  }


  cancelDelete(): void {
    this.deleteRequest = null;
  }


  trackById(
    _: number,
    item: { id: number }
  ): number {
    return item.id;
  }


  private uploadWhitelistDocument(
    id: number,
    file: File
  ): void {
    this.whitelistService
      .uploadDocument(id, file)
      .subscribe({
        next: () => {
          this.finishWhitelistSave();
        },

        error: error => {
          console.error(
            'Portal resource saved, but PDF upload failed:',
            error
          );

          this.selectedWhitelistDocument =
            null;

          this.whitelistEditorOpen = false;

          this.loadWhitelistItems();

          this.toastService.error(
            'Resource saved, but the PDF upload failed.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }


  private finishWhitelistSave(): void {
    this.selectedWhitelistDocument = null;

    this.whitelistDraft =
      this.emptyWhitelistItem();

    this.whitelistEditorOpen = false;

    this.loadWhitelistItems();

    this.toastService.success(
      'Portal resource saved.'
    );

    this.changeDetector.markForCheck();
  }


  private deleteWhitelistItem(
    id: number
  ): void {
    this.whitelistService
      .delete(id)
      .subscribe({
        next: () => {
          this.whitelistItems =
            this.whitelistItems.filter(
              item =>
                item.id !== id
            );

          this.deleteRequest = null;

          this.toastService.success(
            'Portal resource deleted.'
          );

          this.changeDetector.markForCheck();
        },

        error: error => {
          console.error(
            'Unable to delete portal resource:',
            error
          );

          this.toastService.error(
            'Unable to delete portal resource.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }


  private emptyWhitelistItem():
    WhitelistItem {
    return {
      id: 0,
      name: '',
      url: '',
      role: 'Crew',
      category: '',
      displayOrder: 0,
      isActive: true,
      documentId: null
    };
  }

}